import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { getProfile, getBirthData } from '../lib/auth';
import './ProfileSetup.css';

const ProfileSetup = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Form data
  const [fullName, setFullName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [birthTimezone, setBirthTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Helsinki');
  const [birthPlaceName, setBirthPlaceName] = useState('');
  const [unknownTime, setUnknownTime] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!supabase) {
        setLoading(false);
        return;
      }

      try {
        // Hae käyttäjä
        const { data: { user: currentUser } } = await supabase.auth.getUser();
        if (!currentUser) {
          setLoading(false);
          return;
        }

        setUser(currentUser);

        // Hae profiili (tarkista onko full_name olemassa)
        const { data: profile } = await getProfile(currentUser.id);
        if (profile?.full_name) {
          setFullName(profile.full_name);
        }

        // Tarkista onko birth_data jo olemassa
        const { data: existingBirthData } = await getBirthData(currentUser.id);
        if (existingBirthData) {
          // Jos birth_data löytyy, ohjaa charts-sivulle
          navigate('/charts', { replace: true });
          return;
        }
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('ProfileSetup: Error:', err);
        }
        setError('Tietojen haku epäonnistui.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    if (!supabase || !user) {
      setError('Et ole kirjautunut.');
      setSaving(false);
      return;
    }

    // Validointi
    if (!birthDate) {
      setError('Syntymäpäivä on pakollinen.');
      setSaving(false);
      return;
    }

    if (!birthPlaceName) {
      setError('Syntymäpaikka on pakollinen.');
      setSaving(false);
      return;
    }

    // Jos unknownTime on true, aseta aika automaattisesti 12:00
    // time ei saa olla null -> aina 12:00 jos unknown
    const finalBirthTime = unknownTime ? '12:00' : (birthTime || '12:00');
    if (!unknownTime && !birthTime) {
      setError('Täytä syntymäaika tai valitse "En tiedä tarkkaa syntymäaikaa".');
      setSaving(false);
      return;
    }

    // Jos full_name puuttuu, se on pakollinen
    if (!fullName) {
      setError('Nimi on pakollinen.');
      setSaving(false);
      return;
    }

    try {
      // Varmista että käyttäjä on oikea
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser || authUser.id !== user.id) {
        throw new Error('Autentikointi epäonnistui');
      }

      // 1. Upsert profiili (varmista että full_name on tallennettuna)
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert(
          {
            id: authUser.id,
            user_id: authUser.id,
            full_name: fullName,
          },
          { onConflict: 'id' }
        );

      if (profileError) {
        throw profileError;
      }

      // 2. Upsert syntymätiedot
      // Runtime assert: birthDate on pakollinen
      if (!birthDate) {
        throw new Error('birthDate is required but was missing');
      }

      // Varmista että date on ISO-muodossa YYYY-MM-DD
      const isoDate = birthDate; // type="date" antaa automaattisesti YYYY-MM-DD
      
      const payload = {
        user_id: authUser.id,
        date: isoDate, // YYYY-MM-DD (NOT NULL)
        time: finalBirthTime || '12:00', // aina arvo, ei null
        location: birthPlaceName, // pakollinen
        timezone: birthTimezone || 'Europe/Helsinki',
        lat: null,
        lng: null,
        birth_time_accuracy: unknownTime ? 'noon' : 'exact',
      };

      // Loggaa payload vain dev-ympäristössä
      if (process.env.NODE_ENV !== 'production') {
        console.log('BIRTH PAYLOAD', payload);
      }

      const { error: birthError } = await supabase
        .from('birth_data')
        .upsert(payload, { onConflict: 'user_id' });

      if (birthError) {
        console.error('Birth data upsert error:', birthError);
        if (process.env.NODE_ENV !== 'production') {
          console.error('Birth data payload:', payload);
        }
        throw birthError;
      }

      // Onnistui! Ohjaa charts-sivulle
      navigate('/charts', { replace: true });
    } catch (err) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('ProfileSetup: Save error:', err);
        console.error('ProfileSetup: Full error object:', JSON.stringify(err, null, 2));
      } else {
        console.error('ProfileSetup: Save error:', err.message || err);
      }
      if (err.message?.includes('RLS') || err.code === '42501') {
        setError('Kirjautuminen vaaditaan. Kirjaudu sisään ja yritä uudelleen.');
      } else {
        setError(`Tallennus epäonnistui: ${err.message || 'Tuntematon virhe'}`);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-setup-page">
        <div className="container">
          <div className="loading" style={{ minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="moon-loader">
              <div className="moon"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="profile-setup-page">
        <div className="container">
          <p>Kirjautuminen tarkistetaan…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="profile-setup-page">
      <div className="container">
        <h1>Täydennä syntymätietosi</h1>
        
        <div className="info-box">
          <p>
            <strong>Astrologinen kartta vaatii tarkan syntymäajan ja -paikan.</strong>
          </p>
          <p>
            Tietoja käytetään vain henkilökohtaisiin tulkintoihin.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="profile-setup-form">
          <div className="form-group">
            <label htmlFor="fullName">Nimi *</label>
            <input
              id="fullName"
              name="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Koko nimesi"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthDate">Syntymäpäivä *</label>
            <input
              id="birthDate"
              name="birthDate"
              type="date"
              value={birthDate}
              onChange={(e) => setBirthDate(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="birthTime">Syntymäaika *</label>
            <input
              id="birthTime"
              name="birthTime"
              type="time"
              value={birthTime}
              onChange={(e) => setBirthTime(e.target.value)}
              disabled={unknownTime}
              required={!unknownTime}
            />
            <div className="checkbox-group" style={{ marginTop: '0.5rem' }}>
              <label htmlFor="unknownTime" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  id="unknownTime"
                  name="unknownTime"
                  type="checkbox"
                  checked={unknownTime}
                  onChange={(e) => {
                    setUnknownTime(e.target.checked);
                    if (e.target.checked) {
                      setBirthTime('12:00');
                    }
                  }}
                />
                <span>En tiedä tarkkaa syntymäaikaa</span>
              </label>
            </div>
            {unknownTime && (
              <p className="info-text" style={{ marginTop: '0.5rem', fontSize: '0.875rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Kun syntymäaika ei ole tiedossa, käytämme klo 12:00 (noon). 
                Tämä vaikuttaa nousumerkkiin ja huonejakoon.
              </p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="birthTimezone">Aikavyöhyke *</label>
            <select
              id="birthTimezone"
              name="birthTimezone"
              value={birthTimezone}
              onChange={(e) => setBirthTimezone(e.target.value)}
              required
            >
              <option value="Europe/Helsinki">Europe/Helsinki</option>
              <option value="Europe/Stockholm">Europe/Stockholm</option>
              <option value="Europe/Oslo">Europe/Oslo</option>
              <option value="Europe/Copenhagen">Europe/Copenhagen</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York</option>
              <option value="America/Los_Angeles">America/Los_Angeles</option>
              <option value="Asia/Tokyo">Asia/Tokyo</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="birthPlaceName">Syntymäpaikka *</label>
            <input
              id="birthPlaceName"
              name="birthPlaceName"
              type="text"
              value={birthPlaceName}
              onChange={(e) => setBirthPlaceName(e.target.value)}
              placeholder="esim. Helsinki, Finland"
              required
            />
          </div>

          <button type="submit" disabled={saving} className="btn btn-primary">
            {saving ? 'Tallennetaan...' : 'Tallenna ja jatka'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfileSetup;
